import React, { useEffect, useState } from "react";
import request from "request"

import Button from "Button";

import RefundConfirmModal from "./RefundConfirmModal"

const SEARCH_DEBOUNCE_MS = 350

const formatCreatedAt = (iso) => {
  if (!iso) return ""
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ""
  return d.toLocaleString()
}

const isInteracPayment = (payment) => payment?.paymentMethodType === "interac_present"

const paymentLabel = (order) => {
  const payment = order.payment
  if (!payment) return "—"
  if (payment.type === "cash") return "Cash"
  if (payment.lastFour) {
    return (
      <span>
        { isInteracPayment(payment) && (
          <span
            className="tag is-info is-light has-text-weight-bold mr-2"
            title="Interac — refund requires the card reader"
          >
            Interac
          </span>
        )}
        •••• {payment.lastFour}
      </span>
    )
  }
  return payment.type || "—"
}

const refundDisabled = (order) => {
  const refundedCents = parseInt(order.refunded || "0", 10) * 100
  if (!order.payment) return true
  if (order.payment.type === "cash") return true
  return false
}

const printOrder = (orderId, setBusyId) => {
  setBusyId(orderId)
  const req = request.post(`/orders/${orderId}/print_receipt`)
  req.finally(() => setBusyId(null))
}

const RefundOrderListView = ({ terminal }) => {
  const [orders, setOrders] = useState([])
  const [pagy, setPagy] = useState({ page: 1, pages: 1, count: 0 })
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState("")
  const [submittedSearch, setSubmittedSearch] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [expanded, setExpanded] = useState({})
  const [busyId, setBusyId] = useState(null)
  const [refundTarget, setRefundTarget] = useState(null)

  const fetchOrders = () => {
    setIsLoading(true)
    const params = new URLSearchParams({ page: String(page) })
    if (submittedSearch) params.set("search", submittedSearch)

    const req = request.get(`/orders/paid_orders?${params.toString()}`)
    req.then(({ data }) => {
      setOrders(data.orders || [])
      if (data.pagy) setPagy(data.pagy)
    })
    req.finally(() => setIsLoading(false))
  }

  useEffect(fetchOrders, [page, submittedSearch])

  useEffect(() => {
    const handle = setTimeout(() => {
      if (submittedSearch !== searchTerm) {
        setPage(1)
        setSubmittedSearch(searchTerm)
      }
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [searchTerm])

  const toggleExpanded = (id) => {
    setExpanded({ ...expanded, [id]: !expanded[id] })
  }

  const handleRefunded = () => {
    setRefundTarget(null)
    fetchOrders()
  }

  return (
    <div className="StoreMenu">
      <div className="mb-4 mt-4">
        <input
          type="text"
          className="input"
          placeholder="Search store orders by order #, item name, or last 4"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="is-flex is-align-items-center mb-4">
        <h3 className="title is-size-4 mb-0 is-flex-grow-1">
          { submittedSearch ? `Store orders matching "${submittedSearch}"` : "Recent paid orders (last 2 days)" }
        </h3>
        { isLoading && <span className="is-size-7 has-text-grey">Loading…</span> }
      </div>

      { orders.length === 0 ? (
        <p className="has-text-centered py-4">{ isLoading ? "" : "No orders found." }</p>
      ) : (
        <table className="table is-fullwidth is-hoverable">
          <thead>
            <tr>
              <th></th>
              <th>Order #</th>
              <th>Created</th>
              <th>Total</th>
              <th>Payment</th>
              <th>Refunded</th>
              <th className="has-text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            { orders.map((order) => {
              const isOpen = !!expanded[order.id]
              return (
                <React.Fragment key={order.id}>
                  <tr>
                    <td>
                      <span
                        className="icon is-clickable"
                        onClick={() => toggleExpanded(order.id)}
                        role="button"
                        aria-label="toggle"
                      >
                        <i className={`fa fa-chevron-${isOpen ? "down" : "right"}`} />
                      </span>
                    </td>
                    <td>#{order.number}</td>
                    <td>{formatCreatedAt(order.completedAt || order.placedAt)}</td>
                    <td>${order.total}</td>
                    <td>{paymentLabel(order)}</td>
                    <td>
                      { order.refunded
                        ? <span className="tag is-warning">Refunded ${order.refunded}</span>
                        : <span className="has-text-grey">—</span>
                      }
                    </td>
                    <td className="has-text-right">
                      <Button
                        text="Print"
                        className="is-outlined mr-2"
                        color="link"
                        loading={busyId === order.id}
                        onClick={() => printOrder(order.id, setBusyId)}
                      />
                      <Button
                        text="Refund"
                        color="red"
                        disabled={refundDisabled(order)}
                        onClick={() => setRefundTarget(order)}
                      />
                    </td>
                  </tr>
                  { isOpen && (
                    <tr className="has-background-light">
                      <td></td>
                      <td colSpan={6}>
                        <OrderDetail order={order} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      )}

      <div className="is-flex is-align-items-center is-justify-content-space-between mt-4">
        <div className="is-size-7 has-text-grey">
          { pagy.count ? `${pagy.count} orders total` : "" }
        </div>
        <div className="is-flex">
          <Button
            text="Previous"
            className="is-outlined mr-2"
            disabled={page <= 1 || isLoading}
            onClick={() => setPage(Math.max(1, page - 1))}
          />
          <span className="is-align-self-center mx-2">
            Page {pagy.page} of {pagy.pages || 1}
          </span>
          <Button
            text="Next"
            className="is-outlined ml-2"
            disabled={page >= (pagy.pages || 1) || isLoading}
            onClick={() => setPage(page + 1)}
          />
        </div>
      </div>

      { refundTarget && (
        <RefundConfirmModal
          order={refundTarget}
          onClose={() => setRefundTarget(null)}
          onRefunded={handleRefunded}
        />
      )}
    </div>
  )
}

const OrderDetail = ({ order }) => {
  const products = order.products || []
  const discounts = order.discountLineItems || []

  return (
    <div className="py-2">
      <h5 className="is-size-6 has-text-weight-bold mb-2">Line items</h5>
      { products.length === 0 && <p className="has-text-grey">No line items</p> }
      { products.length > 0 && (
        <table className="table is-fullwidth is-narrow is-borderless">
          <thead>
            <tr>
              <th>Item</th>
              <th>Qty</th>
              <th className="has-text-right">Price</th>
            </tr>
          </thead>
          <tbody>
            { products.map((li) => (
              <tr key={li.id}>
                <td>{li.productName || li.name}</td>
                <td>{li.quantity}</td>
                <td className="has-text-right">${li.totalPrice || li.price}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      { discounts.length > 0 && (
        <>
          <h5 className="is-size-6 has-text-weight-bold mt-3 mb-2">Discounts</h5>
          <ul>
            { discounts.map((d) => (
              <li key={d.id}>
                { d.discount?.name || "Discount" } — ${d.discountAmount || d.amount || "0"}
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="mt-3 is-flex is-flex-wrap-wrap">
        <span className="mr-4"><strong>Subtotal:</strong> ${order.subtotal}</span>
        <span className="mr-4"><strong>Tax:</strong> ${order.tax}</span>
        { order.refunded && (
          <span className="mr-4 has-text-warning-dark">
            <strong>Refunded:</strong> ${order.refunded}
          </span>
        )}
      </div>
    </div>
  )
}

export default RefundOrderListView
